import React from 'react'
import { When } from 'react-if'
import { InternalLink } from '@/atoms/InternalLink'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { CaptionMD, CaptionXS } from '@/atoms/Text'
import { PRICE_ANCHOR_EXPERIMENT_VARIANTS } from '@/constants/guild'
import { paths } from '@/constants/paths'
import { RegionalAvailabilityWarning } from '@/organisms/RegionalAvailabilityWarning'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'
import { OmniGuildPlanSelectionCard } from '../GuildJoinView/OmniGuildPlanSelectionCard'

interface GuildPricingViewProps {
  heading: React.ReactNode
  isSignupLayout?: boolean
  canShowFreeOption?: boolean
  isOnlyBasic?: boolean
}

export const GuildPricingView: React.FC<GuildPricingViewProps> = ({ heading, isSignupLayout, canShowFreeOption }) => {
  const { shouldShowAvailabilityWarning, experiments } = useGuildSignupContext()
  const { t } = useTranslate('guild')
  const showWatchlist = Boolean(experiments['free-signup-flow-watchlist'])

  return (
    <main className="h-auto overflow-y-auto bg-core-gray-200 pb-10 scrollbar-thin scrollbar-track-core-gray-200 scrollbar-thumb-core-gray-500 md:h-screen md:pb-0">
      <section className="h-full min-h-[100dvh] w-full">
        <div className="hidden py-3 sm:mx-6 sm:py-4 md:flex md:justify-start">
          <InternalLink aria-label="Angel" href={paths.home}>
            <AngelLogo height={40} color="black" />
          </InternalLink>
        </div>
        <div>
          {heading}

          <div className="mt-4 w-full md:mt-8">
            <When condition={shouldShowAvailabilityWarning}>
              <RegionalAvailabilityWarning className="mx-auto w-fit max-w-[800px]" />
            </When>
            <OmniGuildPlanSelectionCard
              className="mt-0"
              darkMode={false}
              hideLabels
              isSignupLayout={isSignupLayout}
              guildOfferPackage={PRICE_ANCHOR_EXPERIMENT_VARIANTS['18-169 / 20-179']}
              defaultSelectedOffer="premium"
            />
          </div>
          <div className="w-full items-center justify-center px-4 py-2 text-center md:py-6">
            <CaptionXS weight="medium" className="md:photon-caption-sm">
              <Translate t={t} i18nKey="fullHdUltraUdAvailabilityDisclaimer">
                Full HD (1080p) and Ultra HD (4K) availability are subject to your internet service and device
                capabilities. Not all content is available in all resolutions.
              </Translate>
            </CaptionXS>
          </div>
          <When condition={canShowFreeOption}>
            <div className="w-full items-center justify-center px-4 pb-10 pt-2 text-center md:pb-[138px] md:pt-6">
              <CaptionMD weight="medium" className="md:photon-caption-lg">
                <Translate t={t} i18nKey="notInterestedPaidJoinFreeShowsFilms">
                  Not interested in a paid membership? - Click{' '}
                  <a href={showWatchlist ? paths.watchlist.index : paths.watch.index} className="underline">
                    here
                  </a>{' '}
                  to watch our free shows & films
                </Translate>
              </CaptionMD>
            </div>
          </When>
        </div>
      </section>
    </main>
  )
}
